/**
 * Working Patterns - Manager UI (simplified setup).
 *
 * Working Patterns are OPTIONAL and act as a display / setup layer
 * only. They record "what is normal for this employee". Rota Style,
 * roster, shift templates and shift patterns remain the source of
 * truth for the actual shifts a business runs.
 *
 * Working Patterns MUST NOT:
 *   - generate shifts
 *   - block Rota Style, roster, or Shift Pattern apply
 *   - decide overnight, split-day, or any actual shift times
 *
 * There are 8 setup templates. They are simple presets over the
 * SAME three backend types (`scheduled_cycle`, `flexible_weekly`,
 * `zero_hours`). Templates only change labels, default values,
 * helper text, which simple fields are shown and default notes.
 * They are NOT separate scheduling engines.
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogClose,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Copy, Archive, Eye, Pencil, X, ArrowLeft,
} from "lucide-react";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ------------------------------------------------------------------
// Setup templates - simple presets over the existing backend types.
// ------------------------------------------------------------------
const SETUP_TEMPLATES = [
  {
    key: "standard_weekly",
    label: "Standard weekly",
    blurb: "Regular office, admin, retail or fixed weekly staff.",
    backendType: "scheduled_cycle",
    defaults: {
      usualDays: [0, 1, 2, 3, 4],
      weeklyHours: 40,
      defaultStart: "09:00",
      cycleLength: 1,
    },
  },
  {
    key: "rotating_schedule",
    label: "Rotating schedule",
    blurb: "Employees who work different weeks in a cycle (e.g. Week 1 day cover, Week 2 night cover).",
    backendType: "scheduled_cycle",
    defaults: {
      usualDays: [0, 1, 2, 3, 4],
      weeklyHours: 40,
      defaultStart: "09:00",
      cycleLength: 2,
      weekLabels: ["Week 1", "Week 2"],
    },
  },
  {
    key: "night_cover",
    label: "Night cover",
    blurb: "For security, care, warehouse, hospitality night teams and 24/7 support. Records employees who normally work overnight; exact overnight shift times are set in Rota Style, roster, shift templates or shift patterns.",
    backendType: "flexible_weekly",
    defaults: {
      usualDays: [0, 1, 2, 3, 4],
      minHours: 30,
      maxHours: 40,
      notes: "Records employees who normally work overnight. Exact overnight shift times are set in Rota Style, roster, shift templates or shift patterns.",
    },
  },
  {
    key: "service_split_day",
    label: "Service / split-day cover",
    blurb: "For hospitality, care, cleaning, events and service teams. Records staff who may work long shifts, split days or unpaid gaps. Exact shift times and Away blocks are set in Rota Style or the roster.",
    backendType: "flexible_weekly",
    defaults: {
      usualDays: [0, 1, 2, 3, 4, 5, 6],
      minHours: 20,
      maxHours: 40,
      notes: "Records staff who may work long shifts, split days or unpaid gaps. Exact shift times and Away blocks are set in Rota Style or the roster.",
    },
  },
  {
    key: "flexible_weekly",
    label: "Flexible weekly",
    blurb: "Employees with a weekly hours range and no fixed daily times.",
    backendType: "flexible_weekly",
    defaults: {
      usualDays: [0, 1, 2, 3, 4],
      minHours: 20,
      maxHours: 30,
    },
  },
  {
    key: "zero_hours",
    label: "Zero-hours / casual",
    blurb: "Casual staff with no guaranteed hours.",
    backendType: "zero_hours",
    defaults: {
      usualDays: [0, 1, 2, 3, 4, 5, 6],
      weeklyCapHours: null,
    },
  },
  {
    key: "weekend_part_time",
    label: "Weekend / part-time",
    blurb: "Staff who mainly work weekends or part-time. All defaults are editable.",
    backendType: "scheduled_cycle",
    defaults: {
      usualDays: [5, 6],
      weeklyHours: 16,
      defaultStart: "10:00",
      cycleLength: 1,
    },
  },
  {
    key: "custom",
    label: "Custom",
    blurb: "Set up a pattern that suits your business. Advanced editor available if you need per-day blocks.",
    backendType: "scheduled_cycle",
    defaults: {
      usualDays: [],
      weeklyHours: 0,
      defaultStart: "09:00",
      cycleLength: 1,
      allowAdvanced: true,
    },
  },
];

// Human labels for the backend `type` field in the list (fallback
// only - used when a legacy pattern has no `setup_template_key`).
const BACKEND_TYPE_LABEL = {
  scheduled_cycle: "Fixed / rotating",
  flexible_weekly: "Flexible weekly",
  zero_hours: "Zero-hours",
};

// Lookup: setup_template_key -> customer-facing label (drives the
// list badge so a manager sees what they picked, not the technical
// subtype).
const SETUP_TEMPLATE_LABEL = SETUP_TEMPLATES.reduce((acc, t) => {
  acc[t.key] = t.label;
  return acc;
}, {});

// Account setup Step 8 quick presets (Feb 2026). Display labels only,
// so patterns created from Account setup show a friendly badge here.
Object.assign(SETUP_TEMPLATE_LABEL, {
  setup_standard_weekdays: "Standard weekdays",
  setup_part_time_mornings: "Part-time mornings",
  setup_part_time_afternoons: "Part-time afternoons",
  setup_weekend_cover: "Weekend cover",
});

// ------------------------------------------------------------------
// Small helpers
// ------------------------------------------------------------------
function hoursLabel(minutes) {
  if (!minutes) return "0h";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export function todayIsoMonday() {
  const d = new Date();
  const day = d.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + mondayOffset);
  return d.toISOString().slice(0, 10);
}

function addMinutesToHhmm(hhmm, mins) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const total = h * 60 + m + mins;
  const nh = Math.floor(total / 60);
  const nm = total % 60;
  return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
}

// Build a scheduled_cycle config from the simple form fields.
// Every "usual day" gets ONE block starting at `defaultStart` with
// duration = weeklyHours / dayCount. Rest days are `is_off=true`.
// Overnight blocks are avoided - if the derived duration would
// cross midnight, we cap start so the block ends by 23:59. Actual
// overnight rosters are set in Rota Style / roster (per product
// rule - Working Patterns do NOT decide overnight times).
export function buildScheduledCycleWeeks(cycleLength, usualDays, weeklyMinutes, defaultStart) {
  const dayCount = Math.max(1, usualDays.length);
  let perDay = Math.round(weeklyMinutes / dayCount);
  if (perDay < 0) perDay = 0;
  // Cap so start+duration <= 24h (Working Patterns never encode overnight).
  const [sh, sm] = (defaultStart || "09:00").split(":").map(Number);
  const startMin = sh * 60 + sm;
  const maxDuration = Math.max(0, 24 * 60 - startMin);
  if (perDay > maxDuration) perDay = maxDuration;
  const weeks = [];
  for (let wi = 0; wi < cycleLength; wi++) {
    const days = [];
    for (let wd = 0; wd < 7; wd++) {
      const isWorking = usualDays.includes(wd) && perDay > 0;
      days.push({
        weekday: wd,
        is_off: !isWorking,
        blocks: isWorking
          ? [{
              start_time: defaultStart || "09:00",
              duration_minutes: perDay,
              blocks_span_midnight: false,
              away_slots: [],
            }]
          : [],
      });
    }
    weeks.push({ week_index: wi, days });
  }
  return weeks;
}

// Given an existing pattern, infer the simple-form fields so we can
// pre-fill the shared form when editing.
function derivePatternSimpleForm(p) {
  if (!p) return null;
  if (p.type === "scheduled_cycle" && p.scheduled_cycle) {
    const sc = p.scheduled_cycle;
    const weeks = sc.weeks || [];
    // Usual days = union of days that carry blocks in ANY week.
    const dayHits = new Set();
    const startTimes = [];
    let totalMinutes = 0;
    let advanced = false;
    weeks.forEach((wk) => {
      (wk.days || []).forEach((d) => {
        const blocks = d.blocks || [];
        if (d.is_off || blocks.length === 0) return;
        dayHits.add(d.weekday);
        if (blocks.length > 1) advanced = true;
        blocks.forEach((b) => {
          if (b.start_time) startTimes.push(b.start_time);
          totalMinutes += b.duration_minutes || 0;
        });
      });
    });
    const usualDays = Array.from(dayHits).sort((a, b) => a - b);
    const cycleLength = sc.cycle_length_weeks || 1;
    const weeklyMinutes = weeks.length > 0 ? Math.round(totalMinutes / weeks.length) : 0;
    // Advanced if start times vary across blocks.
    const uniqueStarts = Array.from(new Set(startTimes));
    if (uniqueStarts.length > 1) advanced = true;
    return {
      usualDays,
      weeklyHours: Math.round((weeklyMinutes / 60) * 100) / 100,
      defaultStart: uniqueStarts[0] || "09:00",
      cycleLength,
      weekLabels: [],
      isAdvancedShape: advanced,
      backendType: "scheduled_cycle",
    };
  }
  if (p.type === "flexible_weekly" && p.flexible_weekly) {
    const fw = p.flexible_weekly;
    return {
      usualDays: fw.working_days || [],
      minHours: Math.round(((fw.min_weekly_minutes || 0) / 60) * 100) / 100,
      maxHours: Math.round(((fw.max_weekly_minutes || 0) / 60) * 100) / 100,
      isAdvancedShape: false,
      backendType: "flexible_weekly",
    };
  }
  if (p.type === "zero_hours" && p.zero_hours) {
    const zh = p.zero_hours;
    return {
      usualDays: zh.working_days || [],
      weeklyCapHours: zh.legal_max_weekly_minutes ? Math.round((zh.legal_max_weekly_minutes / 60) * 100) / 100 : null,
      isAdvancedShape: false,
      backendType: "zero_hours",
    };
  }
  return null;
}

// ------------------------------------------------------------------
// Main page
// ------------------------------------------------------------------
export default function WorkingPatternsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [picker, setPicker] = useState(false);
  const [editor, setEditor] = useState({ open: false, editing: null, template: null });
  const [previewFor, setPreviewFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/patterns?include_archived=${includeArchived}`);
      setRows(data || []);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not load patterns");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [includeArchived]);

  const onArchive = async (row) => {
    if (!confirm(`Archive "${row.name}"? Existing assignments stay in place, but this pattern will hide from the picker.`)) return;
    try {
      await api.post(`/patterns/${row.pattern_id}/archive`);
      toast.success("Pattern archived");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Archive failed");
    }
  };

  const onDuplicate = async (row) => {
    try {
      const { data } = await api.post(`/patterns/${row.pattern_id}/duplicate`);
      toast.success(`Duplicated as "${data.name}"`);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Duplicate failed");
    }
  };

  const openTemplatePicker = () => setPicker(true);
  const chooseTemplate = (tpl) => {
    setPicker(false);
    setEditor({ open: true, editing: null, template: tpl });
  };
  const openEditor = (row) => {
    setEditor({ open: true, editing: row, template: null });
  };

  return (
    <div data-testid="working-patterns-page">
      <div className="flex flex-wrap items-end justify-between mb-6 gap-3">
        <div>
          <h1 className="text-3xl font-semibold">Working Patterns</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Working Patterns are optional. They record what is normal for each employee. Rota Style, the roster, shift templates and shift patterns continue to run the actual shifts.
          </p>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl" data-testid="wp-page-helper-copy">
            Use this for the employee&apos;s usual weekly hours and first-week pattern. You can set rotating shifts later in Roster or Rota Style.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <Switch
              data-testid="wp-toggle-archived"
              checked={includeArchived}
              onCheckedChange={setIncludeArchived} />
            Show archived
          </label>
          <Button data-testid="wp-create-button" onClick={openTemplatePicker}>
            <Plus className="h-4 w-4 mr-1" /> New pattern
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading patterns...</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border p-6 text-sm text-muted-foreground">
          No patterns yet. Click <em>New pattern</em> to create one from a simple template.
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm" data-testid="wp-list-table">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Details</th>
                <th className="px-4 py-2 font-medium text-right">Assigned</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.pattern_id}
                    data-testid={`wp-row-${p.pattern_id}`}
                    className="border-t">
                  <td className="px-4 py-3">
                    <div className="font-medium">{p.name}</div>
                    {p.description && (
                      <div className="text-xs text-muted-foreground line-clamp-1 max-w-md">
                        {p.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="secondary" data-testid={`wp-type-${p.pattern_id}`}>
                      {SETUP_TEMPLATE_LABEL[p.setup_template_key] || BACKEND_TYPE_LABEL[p.type] || p.type}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {p.type === "scheduled_cycle" && (
                      <>
                        Cycle: {p.scheduled_cycle?.cycle_length_weeks} week{p.scheduled_cycle?.cycle_length_weeks === 1 ? "" : "s"}
                        <br />
                        Starts: {p.scheduled_cycle?.cycle_start_date}
                      </>
                    )}
                    {p.type === "flexible_weekly" && (
                      <>Weekly {hoursLabel(p.flexible_weekly?.min_weekly_minutes)} - {hoursLabel(p.flexible_weekly?.max_weekly_minutes)}</>
                    )}
                    {p.type === "zero_hours" && (
                      p.zero_hours?.legal_max_weekly_minutes
                        ? `Weekly cap: ${hoursLabel(p.zero_hours.legal_max_weekly_minutes)}`
                        : "No weekly cap"
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {p.active_assignment_count || 0}
                  </td>
                  <td className="px-4 py-3">
                    {p.is_archived ? <Badge variant="outline">Archived</Badge> : <Badge>Active</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost"
                              data-testid={`wp-preview-${p.pattern_id}`}
                              onClick={() => setPreviewFor(p)}
                              title="Preview 4 weeks">
                        <Eye className="h-4 w-4" />
                      </Button>
                      {!p.is_archived && (
                        <Button size="sm" variant="ghost"
                                data-testid={`wp-edit-${p.pattern_id}`}
                                onClick={() => openEditor(p)}
                                title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost"
                              data-testid={`wp-duplicate-${p.pattern_id}`}
                              onClick={() => onDuplicate(p)}
                              title="Duplicate">
                        <Copy className="h-4 w-4" />
                      </Button>
                      {!p.is_archived && (
                        <Button size="sm" variant="ghost"
                                data-testid={`wp-archive-${p.pattern_id}`}
                                onClick={() => onArchive(p)}
                                title="Archive">
                          <Archive className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {picker && (
        <TemplatePickerDialog
          onClose={() => setPicker(false)}
          onChoose={chooseTemplate}
        />
      )}

      {editor.open && (
        <PatternEditor
          editing={editor.editing}
          template={editor.template}
          onBack={() => {
            // On new-pattern flow, going back returns to the picker.
            setEditor({ open: false, editing: null, template: null });
            if (!editor.editing) setPicker(true);
          }}
          onClose={() => setEditor({ open: false, editing: null, template: null })}
          onSaved={() => { setEditor({ open: false, editing: null, template: null }); load(); }}
        />
      )}

      {previewFor && (
        <PatternPreviewDialog pattern={previewFor} onClose={() => setPreviewFor(null)} />
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Template picker dialog - 8 simple presets
// ------------------------------------------------------------------
function TemplatePickerDialog({ onClose, onChoose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="wp-template-picker">
        <DialogHeader>
          <DialogTitle>Choose a setup template</DialogTitle>
          <DialogDescription>
            Templates are simple presets. They set sensible defaults you can change. They do not create shifts on the roster.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SETUP_TEMPLATES.map((t) => (
            <button
              key={t.key}
              data-testid={`wp-template-${t.key}`}
              onClick={() => onChoose(t)}
              className="text-left rounded-md border p-3 hover:border-slate-400 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 transition"
            >
              <div className="flex items-center justify-between">
                <div className="font-medium">{t.label}</div>
              </div>
              <div className="text-xs text-muted-foreground mt-1">{t.blurb}</div>
            </button>
          ))}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" data-testid="wp-template-cancel">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------
// Shared simple Pattern editor
// ------------------------------------------------------------------
function PatternEditor({ editing, template, onClose, onBack, onSaved }) {
  // Simple form state.
  const initial = useMemo(() => {
    if (editing) {
      const derived = derivePatternSimpleForm(editing) || {
        usualDays: [], weeklyHours: 0, defaultStart: "09:00", cycleLength: 1,
        backendType: editing.type || "scheduled_cycle",
      };
      return {
        name: editing.name || "",
        notes: editing.description || "",
        backendType: derived.backendType,
        usualDays: derived.usualDays || [],
        weeklyHours: derived.weeklyHours || 0,
        defaultStart: derived.defaultStart || "09:00",
        cycleLength: derived.cycleLength || 1,
        weekLabels: [],
        minHours: derived.minHours ?? 0,
        maxHours: derived.maxHours ?? 0,
        weeklyCapHours: derived.weeklyCapHours ?? null,
        allowAdvanced: derived.isAdvancedShape,
        advancedMode: !!derived.isAdvancedShape,
        // If we opened an advanced shape, keep the original weeks intact.
        rawScheduledCycle: editing.scheduled_cycle || null,
        rawFlexibleWeekly: editing.flexible_weekly || null,
        rawZeroHours: editing.zero_hours || null,
        cycleStartDate: editing.scheduled_cycle?.cycle_start_date || todayIsoMonday(),
        rsaAutoGenerate: !!editing.rsa_auto_generate,
        // Preserve the customer-facing template key round-trip
        // (passive display / reporting metadata; never touches
        // any automation).
        setupTemplateKey: editing.setup_template_key || null,
      };
    }
    // New pattern - defaults come from the picked template.
    const d = template?.defaults || {};
    return {
      name: "",
      notes: d.notes || "",
      backendType: template?.backendType || "scheduled_cycle",
      usualDays: d.usualDays || [],
      weeklyHours: d.weeklyHours ?? 0,
      defaultStart: d.defaultStart || "09:00",
      cycleLength: d.cycleLength || 1,
      weekLabels: d.weekLabels || [],
      minHours: d.minHours ?? 20,
      maxHours: d.maxHours ?? 40,
      weeklyCapHours: d.weeklyCapHours ?? null,
      allowAdvanced: !!d.allowAdvanced,
      advancedMode: false,
      rawScheduledCycle: null,
      rawFlexibleWeekly: null,
      rawZeroHours: null,
      cycleStartDate: todayIsoMonday(),
      rsaAutoGenerate: false,
      setupTemplateKey: template?.key || null,
    };
  }, [editing, template]);

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  const toggleDay = (wd) => {
    setForm((f) => {
      const has = f.usualDays.includes(wd);
      const next = has ? f.usualDays.filter((x) => x !== wd) : [...f.usualDays, wd].sort((a, b) => a - b);
      return { ...f, usualDays: next };
    });
  };

  const updateWeekLabel = (idx, value) => {
    setForm((f) => {
      const next = [...(f.weekLabels || [])];
      while (next.length < f.cycleLength) next.push("");
      next[idx] = value;
      return { ...f, weekLabels: next };
    });
  };

  const canSave = form.name.trim().length > 0 && !saving;

  const templateLabel = template?.label || (editing ? "" : "Custom");

  // Preview line: for scheduled_cycle, show approximate daily duration + window.
  const previewLine = useMemo(() => {
    if (form.backendType === "scheduled_cycle") {
      const dayCount = form.usualDays.length;
      if (dayCount === 0 || !form.weeklyHours) return "";
      const perDayHours = Math.round((form.weeklyHours / dayCount) * 100) / 100;
      if (form.cycleLength > 1) {
        // Rotating: usual start time is hidden, so we don't show a
        // fake "09:00 - 17:00" window that would mislead a day /
        // night rotation. Actual shift times live in Rota Style /
        // roster.
        return `Rotating across ${form.cycleLength} weeks. Averages ${perDayHours}h per working day, ${dayCount} day${dayCount === 1 ? "" : "s"} per week.`;
      }
      const perDayMin = Math.round(perDayHours * 60);
      const endWindow = addMinutesToHhmm(form.defaultStart, perDayMin);
      return `Typical day: ${form.defaultStart} - ${endWindow} (${perDayHours}h). Working ${dayCount} day${dayCount === 1 ? "" : "s"} per week.`;
    }
    if (form.backendType === "flexible_weekly") {
      return `Weekly range: ${form.minHours}h - ${form.maxHours}h.`;
    }
    if (form.backendType === "zero_hours") {
      return form.weeklyCapHours ? `Optional weekly cap: ${form.weeklyCapHours}h.` : "No weekly cap.";
    }
    return "";
  }, [form]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.notes?.trim() || null,
        type: form.backendType,
        scheduled_cycle: null,
        flexible_weekly: null,
        zero_hours: null,
        is_active: editing?.is_active !== false,
        is_archived: false,
        // Preserve prior value if editing; we no longer expose this UI.
        rsa_auto_generate: !!form.rsaAutoGenerate,
        // Passive display / reporting metadata (Feb 2026). Records
        // which customer-facing setup template the manager picked
        // so the list badge shows "Night cover" instead of the
        // technical "Flexible weekly" subtype. Never read by RSA,
        // Rota Style Apply, Shift Pattern apply, the materialiser
        // or any automation write path.
        setup_template_key: form.setupTemplateKey || null,
      };
      if (form.backendType === "scheduled_cycle") {
        // Advanced shape (existing custom pattern edited without changes)
        // is preserved as-is when the manager left the advanced editor on.
        if (form.advancedMode && form.rawScheduledCycle) {
          payload.scheduled_cycle = {
            ...form.rawScheduledCycle,
            cycle_length_weeks: form.cycleLength,
            cycle_start_date: form.cycleStartDate,
          };
        } else {
          const weeklyMinutes = Math.round(Number(form.weeklyHours || 0) * 60);
          payload.scheduled_cycle = {
            cycle_length_weeks: form.cycleLength,
            cycle_start_date: form.cycleStartDate,
            weeks: buildScheduledCycleWeeks(
              form.cycleLength,
              form.usualDays,
              weeklyMinutes,
              form.defaultStart,
            ),
          };
        }
      } else if (form.backendType === "flexible_weekly") {
        payload.flexible_weekly = {
          working_days: form.usualDays.length > 0 ? form.usualDays : null,
          min_weekly_minutes: Math.round(Number(form.minHours || 0) * 60),
          max_weekly_minutes: Math.round(Number(form.maxHours || 0) * 60),
          default_block_hint: null,
        };
      } else if (form.backendType === "zero_hours") {
        payload.zero_hours = {
          legal_max_weekly_minutes: form.weeklyCapHours
            ? Math.round(Number(form.weeklyCapHours) * 60)
            : null,
          min_daily_rest_minutes: null,
          // Optional available days (Feb 2026). Passive display /
          // reporting metadata - never read by automation.
          working_days: form.usualDays.length > 0 ? form.usualDays : null,
        };
      }

      if (editing?.pattern_id) {
        await api.patch(`/patterns/${editing.pattern_id}`, payload);
        toast.success("Pattern updated");
      } else {
        await api.post("/patterns", payload);
        toast.success("Pattern created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="wp-editor-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {!editing && (
              <Button variant="ghost" size="sm" onClick={onBack} data-testid="wp-editor-back">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <div>
              <DialogTitle>
                {editing ? "Edit pattern" : `New pattern - ${templateLabel}`}
              </DialogTitle>
              <DialogDescription>
                {editing
                  ? "Changes apply to future assignments. Employees on this pattern continue against their current effective assignment."
                  : (template?.blurb || "Fill in the essentials. You can refine later.")}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Name + notes (always shown) */}
        <div className="grid grid-cols-1 gap-3">
          <div>
            <Label>Pattern name</Label>
            <Input
              data-testid="wp-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Nights - 40h"
            />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea
              data-testid="wp-notes"
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Short note for other managers."
              rows={2}
            />
          </div>
        </div>

        {/* Scheduled cycle simple form */}
        {form.backendType === "scheduled_cycle" && !form.advancedMode && (
          <div className="space-y-3 mt-3">
            <p className="text-xs text-muted-foreground rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
               data-testid="wp-editor-helper-copy">
              Use this for the employee&apos;s usual weekly hours and first-week pattern. You can set rotating shifts later in Roster or Rota Style.
            </p>
            <div>
              <Label>Usual working days</Label>
              <div className="flex gap-1 flex-wrap mt-1" data-testid="wp-usual-days">
                {WEEKDAYS.map((label, wd) => {
                  const active = form.usualDays.includes(wd);
                  return (
                    <button
                      type="button"
                      key={wd}
                      onClick={() => toggleDay(wd)}
                      data-testid={`wp-day-chip-${wd}`}
                      className={`px-3 py-1 rounded-full border text-xs transition ${
                        active
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className={`grid grid-cols-1 ${form.cycleLength > 1 ? "md:grid-cols-2" : "md:grid-cols-3"} gap-3`}>
              <div>
                <Label>Expected weekly hours</Label>
                <Input
                  data-testid="wp-weekly-hours"
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.weeklyHours}
                  onChange={(e) => setForm({ ...form, weeklyHours: Number(e.target.value || 0) })}
                />
              </div>
              {form.cycleLength === 1 && (
                <div>
                  <Label>Usual start time (optional guidance)</Label>
                  <Input
                    data-testid="wp-default-start"
                    type="time"
                    value={form.defaultStart}
                    onChange={(e) => setForm({ ...form, defaultStart: e.target.value })}
                  />
                  <div className="text-xs text-muted-foreground mt-1">
                    Guidance only. Actual shift times are set in Rota Style or the roster.
                  </div>
                </div>
              )}
              <div>
                <Label>Cycle length (weeks)</Label>
                <Input
                  data-testid="wp-cycle-length"
                  type="number"
                  min="1"
                  max="12"
                  value={form.cycleLength}
                  onChange={(e) => {
                    const n = Math.max(1, Math.min(12, Number(e.target.value || 1)));
                    setForm({ ...form, cycleLength: n });
                  }}
                />
              </div>
            </div>

            {form.cycleLength > 1 && (
              <div>
                <Label>Week labels (optional)</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
                  {Array.from({ length: form.cycleLength }, (_, i) => (
                    <Input
                      key={i}
                      data-testid={`wp-week-label-${i}`}
                      placeholder={`Week ${i + 1} label (e.g. Day cover)`}
                      value={(form.weekLabels || [])[i] || ""}
                      onChange={(e) => updateWeekLabel(i, e.target.value)}
                    />
                  ))}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Labels are for your team&apos;s clarity only. Actual shifts are set in Rota Style or the roster.
                </div>
              </div>
            )}

            <div>
              <Label>Cycle start date (anchors Week 1)</Label>
              <Input
                data-testid="wp-cycle-start"
                type="date"
                value={form.cycleStartDate}
                onChange={(e) => setForm({ ...form, cycleStartDate: e.target.value })}
              />
            </div>

            {previewLine && (
              <div className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid="wp-preview-line">
                {previewLine}
              </div>
            )}

            {form.allowAdvanced && (
              <div className="pt-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch
                    data-testid="wp-advanced-toggle"
                    checked={form.advancedMode}
                    onCheckedChange={(v) => setForm({ ...form, advancedMode: v })}
                  />
                  Advanced editor (per-day blocks)
                </label>
              </div>
            )}
          </div>
        )}

        {/* Advanced editor - only shown when explicitly toggled on for Custom or on legacy advanced patterns */}
        {form.backendType === "scheduled_cycle" && form.advancedMode && (
          <AdvancedScheduledCycleEditor
            form={form}
            setForm={setForm}
          />
        )}

        {/* Flexible weekly simple form */}
        {form.backendType === "flexible_weekly" && (
          <div className="space-y-3 mt-3">
            <div>
              <Label>Usual available days (optional)</Label>
              <div className="flex gap-1 flex-wrap mt-1" data-testid="wp-usual-days-flex">
                {WEEKDAYS.map((label, wd) => {
                  const active = form.usualDays.includes(wd);
                  return (
                    <button
                      type="button"
                      key={wd}
                      onClick={() => toggleDay(wd)}
                      data-testid={`wp-day-chip-flex-${wd}`}
                      className={`px-3 py-1 rounded-full border text-xs transition ${
                        active
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Leave all off if any day is fine.
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Minimum weekly hours</Label>
                <Input
                  data-testid="wp-min-hours"
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.minHours}
                  onChange={(e) => setForm({ ...form, minHours: Number(e.target.value || 0) })}
                />
              </div>
              <div>
                <Label>Maximum weekly hours</Label>
                <Input
                  data-testid="wp-max-hours"
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.maxHours}
                  onChange={(e) => setForm({ ...form, maxHours: Number(e.target.value || 0) })}
                />
              </div>
            </div>
            {previewLine && (
              <div className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid="wp-preview-line">
                {previewLine}
              </div>
            )}
          </div>
        )}

        {/* Zero-hours simple form */}
        {form.backendType === "zero_hours" && (
          <div className="space-y-3 mt-3">
            <div>
              <Label>Available days (optional)</Label>
              <div className="flex gap-1 flex-wrap mt-1" data-testid="wp-usual-days-zh">
                {WEEKDAYS.map((label, wd) => {
                  const active = form.usualDays.includes(wd);
                  return (
                    <button
                      type="button"
                      key={wd}
                      onClick={() => toggleDay(wd)}
                      data-testid={`wp-day-chip-zh-${wd}`}
                      className={`px-3 py-1 rounded-full border text-xs transition ${
                        active
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Optional. Leave all off if any day works. No guaranteed hours are implied.
              </div>
            </div>
            <div>
              <Label>Weekly cap (optional, hours)</Label>
              <Input
                data-testid="wp-weekly-cap"
                type="number"
                min="0"
                step="1"
                value={form.weeklyCapHours ?? ""}
                onChange={(e) => setForm({
                  ...form,
                  weeklyCapHours: e.target.value === "" ? null : Number(e.target.value),
                })}
              />
              <div className="text-xs text-muted-foreground mt-1">
                Leave blank for no cap. UK statutory maximum is 48h.
              </div>
            </div>
            {previewLine && (
              <div className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid="wp-preview-line">
                {previewLine}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" data-testid="wp-cancel">Cancel</Button>
          </DialogClose>
          <Button onClick={save} disabled={!canSave} data-testid="wp-save">
            {saving ? "Saving..." : (editing ? "Save changes" : "Create pattern")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------
// Advanced per-day editor - shown only when Custom + toggle ON,
// or when editing a legacy pattern that already has advanced shape.
// This is an escape hatch. Not shown by default anywhere else.
// ------------------------------------------------------------------
function emptyBlock() {
  return {
    start_time: "09:00",
    duration_minutes: 480,
    blocks_span_midnight: false,
    away_slots: [],
  };
}

function emptyDay(weekday) {
  return { weekday, is_off: weekday >= 5, blocks: [] };
}

function emptyWeek(weekIndex) {
  return { week_index: weekIndex, days: Array.from({ length: 7 }, (_, i) => emptyDay(i)) };
}

function AdvancedScheduledCycleEditor({ form, setForm }) {
  const sc = form.rawScheduledCycle || {
    cycle_length_weeks: form.cycleLength,
    cycle_start_date: form.cycleStartDate,
    weeks: Array.from({ length: form.cycleLength }, (_, i) => emptyWeek(i)),
  };
  const [activeWeek, setActiveWeek] = useState(0);

  const commit = (nextSc) => {
    setForm({ ...form, rawScheduledCycle: nextSc });
  };

  const setDay = (weekIndex, weekday, patch) => {
    const weeks = (sc.weeks || []).map((w, i) => {
      if (i !== weekIndex) return w;
      const days = (w.days || []).map((d) => (d.weekday === weekday ? { ...d, ...patch } : d));
      return { ...w, days };
    });
    commit({ ...sc, weeks });
  };

  const setBlock = (weekIndex, weekday, blockIndex, patch) => {
    const weeks = (sc.weeks || []).map((w, i) => {
      if (i !== weekIndex) return w;
      const days = (w.days || []).map((d) => {
        if (d.weekday !== weekday) return d;
        const blocks = (d.blocks || []).map((b, bi) => (bi === blockIndex ? { ...b, ...patch } : b));
        return { ...d, blocks };
      });
      return { ...w, days };
    });
    commit({ ...sc, weeks });
  };

  const addBlock = (weekIndex, weekday) => {
    const weeks = (sc.weeks || []).map((w, i) => {
      if (i !== weekIndex) return w;
      const days = (w.days || []).map((d) => {
        if (d.weekday !== weekday) return d;
        return { ...d, is_off: false, blocks: [...(d.blocks || []), emptyBlock()] };
      });
      return { ...w, days };
    });
    commit({ ...sc, weeks });
  };

  const removeBlock = (weekIndex, weekday, blockIndex) => {
    const weeks = (sc.weeks || []).map((w, i) => {
      if (i !== weekIndex) return w;
      const days = (w.days || []).map((d) => {
        if (d.weekday !== weekday) return d;
        return { ...d, blocks: (d.blocks || []).filter((_, bi) => bi !== blockIndex) };
      });
      return { ...w, days };
    });
    commit({ ...sc, weeks });
  };

  return (
    <div className="mt-3 space-y-3 border-t pt-3" data-testid="wp-advanced-editor">
      <div className="text-xs text-muted-foreground">
        Advanced expectations are for setup guidance only. Exact overnight shifts, split shifts and Away blocks are managed in Rota Style, roster, shift templates or shift patterns.
      </div>
      {(sc.weeks || []).length > 1 && (
        <div className="flex gap-1 flex-wrap">
          {(sc.weeks || []).map((w) => (
            <Button
              key={w.week_index}
              size="sm"
              variant={activeWeek === w.week_index ? "default" : "outline"}
              data-testid={`wp-week-tab-${w.week_index}`}
              onClick={() => setActiveWeek(w.week_index)}
            >
              Week {w.week_index + 1}
            </Button>
          ))}
        </div>
      )}
      <div className="space-y-2">
        {(sc.weeks || [])[activeWeek]?.days?.map((d) => (
          <div key={d.weekday}
                className="rounded-md border px-3 py-2"
                data-testid={`wp-day-${activeWeek}-${d.weekday}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 font-medium text-sm">{WEEKDAYS[d.weekday]}</div>
                <label className="flex items-center gap-2 text-xs">
                  <Switch
                    data-testid={`wp-day-off-${activeWeek}-${d.weekday}`}
                    checked={!!d.is_off}
                    onCheckedChange={(v) => setDay(activeWeek, d.weekday, {
                      is_off: v,
                      blocks: v ? [] : (d.blocks || []),
                    })}
                  />
                  Off
                </label>
              </div>
              {!d.is_off && (d.blocks || []).length === 0 && (
                <Button size="sm" variant="ghost"
                        data-testid={`wp-add-block-${activeWeek}-${d.weekday}`}
                        onClick={() => addBlock(activeWeek, d.weekday)}>
                  <Plus className="h-3 w-3 mr-1" /> Add block
                </Button>
              )}
            </div>
            {!d.is_off && (d.blocks || []).length > 0 && (
              <div className="mt-2 space-y-2">
                {(d.blocks || []).map((b, bi) => (
                  <div key={bi}
                        className="grid grid-cols-1 md:grid-cols-4 gap-2 items-center bg-muted/30 rounded px-2 py-2"
                        data-testid={`wp-block-${activeWeek}-${d.weekday}-${bi}`}>
                    <div>
                      <div className="text-xs text-muted-foreground mb-0.5">Start</div>
                      <Input
                        type="time"
                        value={b.start_time}
                        onChange={(e) => setBlock(activeWeek, d.weekday, bi, { start_time: e.target.value })}
                        data-testid={`wp-block-start-${activeWeek}-${d.weekday}-${bi}`}
                      />
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-0.5">Duration (h)</div>
                      <Input
                        type="number"
                        step="0.25"
                        min="0"
                        max="16"
                        value={b.duration_minutes / 60}
                        onChange={(e) => setBlock(activeWeek, d.weekday, bi, {
                          duration_minutes: Math.max(0, Math.round(Number(e.target.value || 0) * 60)),
                        })}
                        data-testid={`wp-block-duration-${activeWeek}-${d.weekday}-${bi}`}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Total: {hoursLabel(b.duration_minutes)}
                    </div>
                    <div className="text-right">
                      <Button size="sm" variant="ghost"
                              onClick={() => removeBlock(activeWeek, d.weekday, bi)}
                              data-testid={`wp-block-remove-${activeWeek}-${d.weekday}-${bi}`}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// 4-week preview dialog (kept as-is - reads the resolver).
// ------------------------------------------------------------------
function PatternPreviewDialog({ pattern, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/patterns/${pattern.pattern_id}/preview?weeks=4`);
        setData(data);
      } catch (err) {
        toast.error(formatError(err.response?.data?.detail) || "Preview failed");
      } finally {
        setLoading(false);
      }
    })();
  }, [pattern.pattern_id]);

  const grouped = useMemo(() => {
    if (!data) return [];
    const out = [];
    (data.days || []).forEach((d, i) => {
      const wk = Math.floor(i / 7);
      if (!out[wk]) out[wk] = [];
      out[wk].push(d);
    });
    return out;
  }, [data]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="wp-preview-dialog">
        <DialogHeader>
          <DialogTitle>Preview - {pattern.name}</DialogTitle>
          <DialogDescription>
            Next 4 calendar weeks resolved from this pattern. This is a preview, not a shift generator.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading...</div>
        ) : (
          <div className="space-y-3">
            {grouped.map((week, wi) => (
              <div key={wi} className="rounded border p-2">
                <div className="text-xs text-muted-foreground mb-1">Week starting {week[0].date}</div>
                <div className="grid grid-cols-7 gap-1 text-xs">
                  {week.map((d) => (
                    <div key={d.date} className="rounded bg-muted/50 p-1.5">
                      <div className="font-medium">{WEEKDAYS[d.weekday]}</div>
                      <div className="text-muted-foreground">{d.date.slice(5)}</div>
                      <div className="mt-1">
                        {d.is_working_day ? (
                          <>
                            <div>{hoursLabel(d.expected_minutes_day)}</div>
                            {d.default_start && (
                              <div className="text-muted-foreground">
                                {d.default_start}{d.default_end ? ` - ${d.default_end}` : ""}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-muted-foreground">Off</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button data-testid="wp-preview-close">Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
